import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ShoppingBag, Sparkles, Drum, Play, ChevronDown, ChevronLeft, ChevronRight, Disc3, User, Menu, X, ExternalLink, Crown, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { FeaturedPacksCarousel } from "@/components/shop/CymbalPacksSection";
import NewsletterSignup from "@/components/NewsletterSignup";
import MembershipSection from "@/components/MembershipSection";
import FAQSection from "@/components/home/FAQSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BoostCredit from "@/components/BoostCredit";
import BlogShowcase from "@/components/BlogShowcase";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Drum Sticks Icon (crossed sticks)
const DrumSticksIcon = ({ className = "h-4 w-4" }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    {/* Two crossed drumsticks */}
    <line x1="4" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="20" y2="4" />
    <circle cx="4.5" cy="19.5" r="1.5" fill="currentColor" />
    <circle cx="6.5" cy="19.5" r="1.5" fill="currentColor" />
  </svg>
);

// Temple of Sound Logo
const TOS_LOGO_WHITE = "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/5zhuaw8g_logo%20full_white.png";

// Brand-specific logos
const ALCHEMIST_LOGO = "/images/opt/alchemist_logo.webp";
const LDC_TEXT_LOGO = "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/44y3q0j5_Text%20Logo%20Gold_noBG.png";
const LDC_WINGS_LOGO = "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/sz7b924w_Gold%20Wings%20and%20Text%20Logo_Stacked_NoBG.png";

// Loyalty Token Logos
const GOLD_DUST_LOGO = "/images/opt/gold_dust.webp";
const BEAT_TOKENS_LOGO = "/images/opt/beat_tokens.webp";

// Epic hero background
const EPIC_BG = "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/ha7oi3lb_Epic%20BG.jpg";

// High-quality hero images from design guidelines
const HERO_IMAGES = {
  main: EPIC_BG,
  ldc: "https://images.pexels.com/photos/2204745/pexels-photo-2204745.jpeg?auto=compress&cs=tinysrgb&w=1920",
  texture: "https://images.pexels.com/photos/5648530/pexels-photo-5648530.jpeg?auto=compress&cs=tinysrgb&w=1920"
};

// Floating Sparks Component
const FloatingSparks = ({ count = 30 }) => {
  const sparks = Array.from({ length: count }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 8,
    duration: 6 + Math.random() * 6,
    size: 2 + Math.random() * 4,
  }));

  return (
    <div className="sparks-container">
      {sparks.map((spark) => (
        <div
          key={spark.id}
          className="spark"
          style={{
            left: spark.left,
            bottom: '-10px',
            width: `${spark.size}px`,
            height: `${spark.size}px`,
            animationDelay: `${spark.delay}s`,
            animationDuration: `${spark.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

// Drummer photos for LDC section
const DRUMMER_PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/ysbotjp2_Maiden%20-%20Copy.jpg", alt: "Nicko McBrain - Iron Maiden" },
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/20nhewgx_Maiden%202%20-%20Copy.jpg", alt: "Nicko McBrain - Iron Maiden" },
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/np3miy69_sweet%20-%20Copy.jpeg", alt: "Sweet drummer performing" },
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/hafvdphu_DANNY%20D.jpg", alt: "Danny D performing live" },
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/rk0lrol6_1.jpg", alt: "VirginMarys drummer" },
  { src: "https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/8i20e9kt_Brixton-blackwhite--scaled.jpg", alt: "Overhead drum kit view" }
];

// Cymbal series showcase
const CYMBAL_SERIES = [
  { name: "OPULENT", image: "/images/opt/opulent.webp" },
  { name: "HELIOS", image: "/images/opt/helios.webp" },
  { name: "ECLIPSE", image: "/images/opt/eclipse.webp" }
];

// Staggered animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

// Featured Artists Carousel Component
const FeaturedArtistsCarousel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  // Calculate how many cards are visible based on screen size
  const getVisibleCards = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 768) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  };
  
  const [visibleCards, setVisibleCards] = useState(3);
  
  useEffect(() => {
    const handleResize = () => setVisibleCards(getVisibleCards());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    const fetchFeaturedArtists = async () => {
      try {
        // Fetch only featured artists from the API
        const response = await fetch(`${API_URL}/api/artists/featured`);
        if (response.ok) {
          const data = await response.json();
          // Filter to only those with photos for visual display
          const withPhotos = data.filter(a => a.photos && a.photos.length > 0);
          setArtists(withPhotos);
        }
      } catch (error) {
        console.error('Failed to fetch featured artists:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeaturedArtists();
  }, []);
  
  // Calculate max index based on visible cards
  const maxIndex = Math.max(0, artists.length - visibleCards);
  
  // Auto-slide
  useEffect(() => {
    if (artists.length <= visibleCards || isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);
    
    return () => clearInterval(interval);
  }, [artists.length, isHovered, maxIndex, visibleCards]);
  
  const goToNext = () => setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  const goToPrev = () => setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  
  if (loading || artists.length === 0) return null;
  
  // Calculate the percentage to move (each card is 1/visibleCards of the container + gap)
  const cardWidthPercent = 100 / visibleCards;
  const gapPercent = (24 / (typeof window !== 'undefined' ? window.innerWidth : 1200)) * 100; // 24px gap approximation
  const translateX = currentIndex * (cardWidthPercent + gapPercent / visibleCards);
  
  return (
    <section className="py-20 lg:py-28 bg-[#080808] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#D4AF37]/5 blur-[120px] rounded-full" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <p 
              className="text-sm text-[#D4AF37] tracking-[0.3em] uppercase mb-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.featured.artistsSubtitle', 'ARTISTS WHO TRUST US')}
            </p>
            <h2 
              className="text-4xl lg:text-5xl text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
            >
              {t('home.featured.title').split(' ')[0]} <span className="text-[#D4AF37]">{t('home.featured.title').split(' ')[1]}</span>
            </h2>
          </div>
          <Link to="/artists">
            <Button
              variant="outline"
              className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
            >
              {t('home.featured.viewAllArtists').toUpperCase()}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
        
        {/* Carousel */}
        <div 
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Navigation arrows */}
          {artists.length > visibleCards && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-3 bg-black/60 border border-white/20 rounded-full text-white hover:bg-black hover:border-[#D4AF37] transition-all"
                aria-label="Previous artist"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-3 bg-black/60 border border-white/20 rounded-full text-white hover:bg-black hover:border-[#D4AF37] transition-all"
                aria-label="Next artist"
              >
                <ChevronRight className="h-6 w-6" aria-hidden="true" />
              </button>
            </>
          )}
          
          {/* Artists slides */}
          <div className="overflow-hidden">
            <motion.div 
              className="flex gap-6"
              animate={{ x: `-${translateX}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {artists.map((artist) => {
                const photo = artist.photos?.[0]?.url || artist.gallery_photos?.[0]?.url;
                
                return (
                  <motion.div
                    key={artist.id}
                    className="flex-shrink-0 w-full md:w-1/2 lg:w-1/3 cursor-pointer group"
                    onClick={() => navigate(`/artists/${artist.slug}`)}
                    whileHover={{ y: -5 }}
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-zinc-900">
                      {photo ? (
                        <img
                          src={photo}
                          alt={artist.full_name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                          <span className="text-6xl text-zinc-600">{artist.full_name?.charAt(0)}</span>
                        </div>
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                      
                      {/* Artist type badge */}
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        {(artist.artist_type === 'alchemist' || artist.artist_type === 'both') && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#D4AF37]/30 backdrop-blur-sm border border-[#D4AF37]/40 rounded-full text-xs text-[#D4AF37]">
                            <Disc3 className="h-3 w-3" />
                          </span>
                        )}
                        {(artist.artist_type === 'ldc' || artist.artist_type === 'both') && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#22C55E]/30 backdrop-blur-sm border border-[#22C55E]/40 rounded-full text-xs text-[#22C55E]">
                            <DrumSticksIcon className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 
                          className="text-2xl text-white font-bold tracking-wide"
                          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
                        >
                          {artist.full_name}
                        </h3>
                        {artist.band_name && (
                          <p className="text-sm text-zinc-400 mt-1">{artist.band_name}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
          
          {/* Dots indicator */}
          {artists.length > visibleCards && (
            <div className="flex items-center justify-center gap-2 mt-8" role="tablist" aria-label="Artist slides">
              {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-[#D4AF37] w-6' : 'bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                  aria-selected={idx === currentIndex}
                  role="tab"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Featured Videos Section Component
const FeaturedVideosSection = () => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/videos/featured?limit=6`);
        if (response.ok) {
          const data = await response.json();
          setVideos(data);
          if (data.length > 0) setSelectedVideo(data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch videos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchVideos();
  }, []);
  
  if (loading || videos.length === 0) return null;
  
  const selectVideo = (video, index) => {
    setSelectedVideo(video);
    setCurrentIndex(index);
  };
  
  const openVideoModal = (video) => {
    setPlayingVideo(video);
    setModalOpen(true);
    // Track view (fire-and-forget, no need to await)
    axios.post(`${API_URL}/api/videos/${video.id}/view`).catch(() => {});
  };
  
  const closeModal = () => {
    setModalOpen(false);
    setPlayingVideo(null);
  };
  
  return (
    <section className="py-20 lg:py-28 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-red-500/5 blur-[150px] rounded-full" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <p 
              className="text-sm text-red-500 tracking-[0.3em] uppercase mb-2"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.videos.latestContent', 'LATEST CONTENT')}
            </p>
            <h2 
              className="text-3xl lg:text-4xl text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
            >
              {t('home.videos.title').split(' ')[0]} <span className="text-red-500">{t('home.videos.title').split(' ')[1]}</span>
            </h2>
          </div>
          <Link 
            to="/videos"
            className="hidden md:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-white transition-colors group"
          >
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>{t('home.videos.viewAllVideos')}</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        
        {/* Videos Layout - Featured + Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Featured Video */}
          <div className="lg:col-span-2">
            {selectedVideo && (
              <motion.div
                key={selectedVideo.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="relative aspect-video rounded-lg overflow-hidden bg-zinc-900 group cursor-pointer"
                onClick={() => openVideoModal(selectedVideo)}
              >
                <img
                  src={selectedVideo.thumbnail_url}
                  alt={selectedVideo.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = `https://img.youtube.com/vi/${selectedVideo.youtube_id}/hqdefault.jpg`;
                  }}
                />
                
                {/* Play Overlay */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-red-600/30">
                    <Play className="h-8 w-8 text-white ml-1" fill="white" />
                  </div>
                </div>
                
                {/* Video Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                  <h3 className="text-xl text-white font-medium mb-1">{selectedVideo.title}</h3>
                  {selectedVideo.artist_name && (
                    <p className="text-red-400">{selectedVideo.artist_name}</p>
                  )}
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Thumbnails Grid */}
          <div className="space-y-3">
            {videos.slice(0, 4).map((video, idx) => (
              <div
                key={video.id}
                onClick={() => selectVideo(video, idx)}
                className={`flex gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                  selectedVideo?.id === video.id 
                    ? 'bg-white/10 border border-red-500/50' 
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="relative w-32 flex-shrink-0 aspect-video rounded overflow-hidden bg-zinc-800">
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="h-6 w-6 text-white" fill="white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm text-white font-medium line-clamp-2">{video.title}</h4>
                  {video.artist_name && (
                    <p className="text-xs text-zinc-500 mt-1">{video.artist_name}</p>
                  )}
                </div>
              </div>
            ))}
            
            {/* View All Link - Mobile */}
            <Link 
              to="/videos"
              className="flex md:hidden items-center justify-center gap-2 text-sm text-[#D4AF37] hover:text-white transition-colors py-3"
            >
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>VIEW ALL VIDEOS</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Video Modal */}
      <AnimatePresence>
        {modalOpen && playingVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={closeModal}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90" />
            
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl"
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 z-[110] w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                aria-label="Close video"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
              
              {/* Video Player */}
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black shadow-2xl">
                <iframe
                  src={`https://www.youtube.com/embed/${playingVideo.youtube_id}?autoplay=1&rel=0`}
                  title={playingVideo.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              
              {/* Video Info */}
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl text-white font-medium">{playingVideo.title}</h2>
                  {playingVideo.artist_name && (
                    <p className="text-red-400 mt-1">{playingVideo.artist_name}</p>
                  )}
                  {playingVideo.description && (
                    <p className="text-zinc-400 mt-2 text-sm max-w-2xl">{playingVideo.description}</p>
                  )}
                </div>
                <a
                  href={`https://www.youtube.com/watch?v=${playingVideo.youtube_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                  YouTube
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default function TempleHome() {
  const { t } = useTranslation();
  const [currentDrummerIndex, setCurrentDrummerIndex] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  
  // Rotate drummer photos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDrummerIndex((prev) => (prev + 1) % DRUMMER_PHOTOS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Check for logged in customer
  useEffect(() => {
    const token = localStorage.getItem("alchemist_customer_token");
    const savedCustomer = localStorage.getItem("alchemist_customer");
    if (token && savedCustomer) {
      try { setCustomer(JSON.parse(savedCustomer)); } catch (e) { console.error(e); }
    }
  }, []);

  // Load cart count from localStorage and listen for changes
  useEffect(() => {
    const updateCartCount = () => {
      try {
        // Count items from both cymbal and drumstick carts
        const cymbalCart = JSON.parse(localStorage.getItem("alchemist_cart") || "[]");
        const ldcCart = JSON.parse(localStorage.getItem("ldc_cart") || "[]");
        const cymbalCount = cymbalCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
        const ldcCount = ldcCart.reduce((sum, item) => sum + (item.quantity || 1), 0);
        setCartItemCount(cymbalCount + ldcCount);
      } catch (e) {
        setCartItemCount(0);
      }
    };
    
    // Initial load
    updateCartCount();
    
    // Listen for storage changes (from other tabs or components)
    window.addEventListener('storage', updateCartCount);
    
    // Custom event for same-tab cart updates
    window.addEventListener('cartUpdated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* SEO Meta Tags */}
      <SEO page="home" />
      
      {/* Glassmorphic Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="group" data-testid="nav-logo" aria-label="Temple of Sound - Home">
              <img 
                src={TOS_LOGO_WHITE} 
                alt="Temple of Sound Logo" 
                className="h-8 sm:h-10 lg:h-12 w-auto"
                width="150"
                height="48"
              />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 lg:gap-8">
              <Link 
                to="/shop" 
                className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="nav-cymbals"
              >
                {t('shop.cymbals')}
              </Link>
              <Link 
                to="/drumsticks" 
                className="text-sm text-zinc-400 hover:text-[#22C55E] transition-colors duration-300"
                data-testid="nav-drumsticks"
              >
                {t('shop.drumsticks')}
              </Link>
              <Link 
                to="/artists" 
                className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                data-testid="nav-artists"
              >
                Artists
              </Link>
              <Link 
                to="/merch" 
                className="text-sm text-zinc-400 hover:text-transparent hover:bg-gradient-to-r hover:from-[#D4AF37] hover:to-[#22C55E] hover:bg-clip-text transition-colors duration-300"
                data-testid="nav-merch"
              >
                {t('nav.merch')}
              </Link>
              <a 
                href="/#memberships" 
                className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="nav-memberships"
              >
                {t('nav.memberships', 'Memberships')}
              </a>
              <Link 
                to="/blog" 
                className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="nav-blog"
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
                data-testid="nav-contact"
              >
                {t('nav.contact', 'Contact')}
              </Link>
              {customer ? (
                <Link 
                  to="/account" 
                  className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 flex items-center gap-1"
                  data-testid="nav-account"
                >
                  <User className="h-4 w-4" />
                  <span>{customer.first_name}</span>
                </Link>
              ) : (
                <Link 
                  to="/customer/auth" 
                  className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                  data-testid="nav-account"
                >
                  {t('common.account')}
                </Link>
              )}
              {/* Language Switcher */}
              <LanguageSwitcher variant="compact" />
              <Link to="/checkout" data-testid="nav-cart" aria-label={`Shopping cart${cartItemCount > 0 ? `, ${cartItemCount} items` : ', empty'}`}>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 rounded-sm transition-all duration-300"
                  aria-label={`View cart${cartItemCount > 0 ? ` (${cartItemCount} items)` : ''}`}
                >
                  <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                  {cartItemCount > 0 && (
                    <span className="ml-2 bg-[#D4AF37] text-black text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center" aria-hidden="true">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </Link>
            </div>

            {/* Mobile Navigation Controls */}
            <div className="flex md:hidden items-center gap-3">
              <Link to="/checkout" data-testid="nav-cart-mobile" aria-label={`Shopping cart${cartItemCount > 0 ? `, ${cartItemCount} items` : ', empty'}`}>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-sm p-2 relative"
                  aria-label={`View cart${cartItemCount > 0 ? ` (${cartItemCount} items)` : ''}`}
                >
                  <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full" aria-hidden="true">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-white hover:bg-white/10 rounded-sm transition-colors"
                data-testid="mobile-menu-btn"
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              id="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
              role="menu"
              aria-label="Mobile navigation menu"
            >
              <div className="px-4 py-4 space-y-1">
                <Link 
                  to="/shop" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                >
                  <Disc3 className="h-5 w-5 text-[#D4AF37]" />
                  <span>{t('shop.cymbals')}</span>
                </Link>
                <Link 
                  to="/drumsticks" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm transition-colors"
                >
                  <Drum className="h-5 w-5 text-[#22C55E]" />
                  <span>{t('shop.drumsticks')}</span>
                </Link>
                <Link 
                  to="/custom-drumsticks" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#22C55E] hover:bg-[#22C55E]/10 rounded-sm transition-colors"
                >
                  <Sparkles className="h-5 w-5 text-[#22C55E]" />
                  <span>Custom Builder</span>
                </Link>
                <Link 
                  to="/blog" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                  data-testid="mobile-nav-blog"
                >
                  <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                  <span>Blog</span>
                </Link>
                <Link 
                  to="/merch" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
                >
                  <ShoppingBag className="h-5 w-5 text-zinc-400" />
                  <span>{t('nav.merch')}</span>
                </Link>
                <Link 
                  to="/artists" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
                >
                  <User className="h-5 w-5 text-zinc-400" />
                  <span>Artists</span>
                </Link>
                <a 
                  href="/#memberships" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                >
                  <Crown className="h-5 w-5 text-[#D4AF37]" />
                  <span>{t('nav.memberships', 'Memberships')}</span>
                </a>
                <Link 
                  to="/contact" 
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-zinc-300 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                  data-testid="nav-contact-mobile"
                >
                  <Mail className="h-5 w-5 text-zinc-400" />
                  <span>{t('nav.contact', 'Contact')}</span>
                </Link>
                
                <div className="border-t border-white/10 pt-3 mt-3">
                  {customer ? (
                    <Link 
                      to="/account" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                    >
                      <User className="h-5 w-5" />
                      <span>{t('nav.myAccount')} ({customer.first_name})</span>
                    </Link>
                  ) : (
                    <Link 
                      to="/customer/auth" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors"
                    >
                      <User className="h-5 w-5" />
                      <span>{t('common.signIn')} / {t('auth.signUp')}</span>
                    </Link>
                  )}
                </div>
                
                {/* Language Switcher - Mobile */}
                <div className="border-t border-white/10 pt-3 mt-3 px-4">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Language</p>
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main id="main-content">
      {/* Hero Section - Full Viewport with Parallax & Floating Sparks */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden" aria-label="Hero banner">
        {/* Parallax Background - Epic BG */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y: heroY }}
          aria-hidden="true"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${EPIC_BG})` }}
            role="img"
            aria-label="Dramatic concert stage with speakers and lighting"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        </motion.div>

        {/* Floating Sparks Overlay */}
        <FloatingSparks count={40} />
        
        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center px-4 sm:px-6"
          style={{ opacity: heroOpacity }}
          role="banner"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <p className="text-xs sm:text-sm lg:text-base tracking-[0.3em] sm:tracking-[0.5em] text-[#D4AF37]/80 mb-4 sm:mb-6 uppercase" style={{ fontFamily: "'Cinzel', serif" }}>
              Est. 2010 • London
            </p>
            <h1 
              className="text-4xl sm:text-6xl md:text-8xl lg:text-[10rem] tracking-wider mb-4 sm:mb-8 leading-none"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.hero.title').split(' ')[0]} {t('home.hero.title').split(' ')[1]}
              <br />
              <span className="bg-gradient-to-r from-[#D4AF37] via-[#F0C84B] to-[#D4AF37] bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(212,175,55,0.5)]">
                {t('home.hero.title').split(' ')[2] || 'SOUND'}
              </span>
            </h1>
            <p className="text-sm sm:text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto mb-8 sm:mb-12 px-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {t('home.hero.subtitle')}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4"
          >
            <Link to="/shop" data-testid="hero-shop-cymbals" className="w-full sm:w-auto" aria-label={t('shop.cymbals')}>
              <Button 
                className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#F0C84B] text-black rounded-sm px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
                aria-label={t('shop.cymbals')}
              >
                {t('common.shopNow').toUpperCase()} {t('shop.cymbals').toUpperCase()}
                <ArrowRight className="h-4 sm:h-5 w-4 sm:w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Button>
            </Link>
            <Link to="/drumsticks" data-testid="hero-shop-drumsticks" className="w-full sm:w-auto" aria-label={t('shop.drumsticks')}>
              <Button 
                variant="outline"
                className="w-full sm:w-auto border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 hover:border-[#22C55E] rounded-sm px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
                aria-label={t('shop.drumsticks')}
              >
                {t('common.shopNow').toUpperCase()} {t('shop.drumsticks').toUpperCase()}
              </Button>
            </Link>
          </motion.div>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          aria-hidden="true"
        >
          <ChevronDown className="h-8 w-8 text-[#D4AF37]/60" aria-hidden="true" />
        </motion.div>
      </section>

      {/* Bento Grid - Brand Showcase */}
      <section className="relative py-24 lg:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="mb-16 lg:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm tracking-[0.4em] text-[#D4AF37]/60 mb-4 uppercase" style={{ fontFamily: "'Cinzel', serif" }}>{t('home.twoLegends.eyebrow')}</p>
            <h2 
              className="text-5xl lg:text-7xl tracking-wider leading-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.twoLegends.title1')}
              <br />
              <span className="text-zinc-500">{t('home.twoLegends.title2')}</span>
            </h2>
          </motion.div>

          {/* Asymmetric Bento Grid */}
          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            {/* Alchemist Cymbals - Large Feature */}
            <motion.div 
              className="col-span-12 lg:col-span-8 relative group"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Link to="/shop" className="block" data-testid="bento-alchemist">
                <div className="relative h-[500px] lg:h-[600px] overflow-hidden rounded-sm border border-[#D4AF37]/20 bg-[#111111]">
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${CYMBAL_SERIES[0].image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="h-5 w-5 text-[#D4AF37]" />
                      <span className="text-sm tracking-[0.3em] text-[#D4AF37] uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>{t('home.brands.premiumCymbals')}</span>
                    </div>
                    <img 
                      src={ALCHEMIST_LOGO} 
                      alt="Alchemist Crafted Cymbals" 
                      className="h-16 lg:h-20 w-auto mb-4 drop-shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                    />
                    <p className="text-zinc-400 text-lg mb-6 max-w-lg" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {t('home.brands.handHammered')}
                    </p>
                    <div className="flex items-center gap-2 text-[#D4AF37] group-hover:gap-4 transition-all duration-300">
                      <span className="text-sm font-medium" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>{t('home.brands.exploreCollection')}</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {/* Gold Accent Line */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D4AF37] via-[#D4AF37]/50 to-transparent" />
                </div>
              </Link>
            </motion.div>

            {/* Alchemist Stats */}
            <motion.div 
              className="col-span-12 sm:col-span-6 lg:col-span-4 flex flex-col gap-4 lg:gap-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div 
                variants={itemVariants}
                className="flex-1 bg-[#111111] border border-[#D4AF37]/10 p-5 sm:p-6 lg:p-8 rounded-sm"
              >
                <p className="text-4xl sm:text-5xl lg:text-6xl text-[#D4AF37] mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  15K+
                </p>
                <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.15em' }}>{t('home.stats.cymbalsAvailable')}</p>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="flex-1 bg-[#111111] border border-[#D4AF37]/10 p-5 sm:p-6 lg:p-8 rounded-sm"
              >
                <p className="text-4xl sm:text-5xl lg:text-6xl text-[#D4AF37] mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  8
                </p>
                <p className="text-zinc-500 text-xs sm:text-sm uppercase tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.15em' }}>{t('home.stats.signatureSeries')}</p>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="flex-1 bg-gradient-to-br from-[#D4AF37]/20 to-[#111111] border border-[#D4AF37]/30 p-5 sm:p-6 lg:p-8 rounded-sm relative overflow-hidden"
              >
                <img src={GOLD_DUST_LOGO} alt="Gold Dust" className="h-16 sm:h-24 w-16 sm:w-24 object-contain mb-4" />
                <p className="text-white text-sm sm:text-base" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{t('home.stats.earnGoldDust')}</p>
              </motion.div>
            </motion.div>

            {/* LDC Drumsticks - Medium Feature */}
            <motion.div 
              className="col-span-12 sm:col-span-6 lg:col-span-5 relative group"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Link to="/drumsticks" className="block" data-testid="bento-ldc-shop">
                <div className="relative h-[400px] lg:h-[500px] overflow-hidden rounded-sm border border-[#22C55E]/20 bg-[#0a1f18]">
                  {/* Drummer Photo Carousel */}
                  <div className="absolute inset-0">
                    {DRUMMER_PHOTOS.map((photo) => (
                      <div
                        key={photo.src}
                        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                          DRUMMER_PHOTOS.indexOf(photo) === currentDrummerIndex ? "opacity-100" : "opacity-0"
                        }`}
                        style={{ backgroundImage: `url(${photo.src})` }}
                      />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f18] via-[#0a1f18]/60 to-transparent" />
                  </div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <Drum className="h-5 w-5 text-[#22C55E]" />
                      <span className="text-sm tracking-[0.3em] text-[#22C55E] uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>{t('shop.drumsticks').toUpperCase()}</span>
                    </div>
                    <img 
                      src={LDC_TEXT_LOGO} 
                      alt="London Drumstick Company" 
                      className="h-10 lg:h-12 w-auto mb-4 drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                    />
                    <div className="flex items-center gap-2 text-[#22C55E] group-hover:gap-4 transition-all duration-300">
                      <span className="text-sm font-medium" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>{t('home.brands.shopDrumsticks')}</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {/* Green Accent */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#22C55E] via-[#22C55E]/50 to-transparent" />
                </div>
              </Link>
            </motion.div>

            {/* Custom Builder CTA */}
            <motion.div 
              className="col-span-6 lg:col-span-4 relative group"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Link to="/custom-drumsticks" className="block h-full" data-testid="bento-custom-builder">
                <div className="relative h-[400px] lg:h-[500px] overflow-hidden rounded-sm border border-[#22C55E]/30 bg-gradient-to-br from-[#0D4D3A] to-[#050505] flex flex-col justify-between p-6 lg:p-8">
                  {/* Grid Pattern Overlay */}
                  <div 
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                                       linear-gradient(90deg, rgba(34, 197, 94, 0.3) 1px, transparent 1px)`,
                      backgroundSize: '40px 40px'
                    }}
                  />
                  
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-sm bg-[#22C55E]/20 border border-[#22C55E]/30 flex items-center justify-center mb-6">
                      <Play className="h-5 w-5 text-[#22C55E]" />
                    </div>
                    <p className="text-sm tracking-[0.3em] text-[#22C55E]/70 uppercase mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>{t('home.customBuilder.eyebrow')}</p>
                    <h3 className="text-3xl lg:text-4xl text-white leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
                      {t('home.customBuilder.title').split(' ').map((word, i) => (
                        <span key={i}>{word}<br /></span>
                      ))}
                    </h3>
                  </div>
                  
                  <div className="relative z-10">
                    <p className="text-zinc-400 text-sm mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {t('home.customBuilder.description')}
                    </p>
                    <div className="flex items-center gap-2 text-[#22C55E] group-hover:gap-4 transition-all duration-300">
                      <span className="text-sm font-medium" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>{t('home.customBuilder.cta')}</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* LDC Stats */}
            <motion.div 
              className="col-span-12 lg:col-span-3 flex flex-row lg:flex-col gap-4"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div 
                variants={itemVariants}
                className="flex-1 bg-[#0a1f18] border border-[#22C55E]/10 p-4 sm:p-6 rounded-sm"
              >
                <p className="text-3xl sm:text-4xl lg:text-5xl text-[#22C55E] mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  14K+
                </p>
                <p className="text-zinc-500 text-xs uppercase tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.15em' }}>{t('home.stats.stickConfigs')}</p>
              </motion.div>
              <motion.div 
                variants={itemVariants}
                className="flex-1 bg-gradient-to-br from-[#22C55E]/20 to-[#0a1f18] border border-[#22C55E]/30 p-4 sm:p-6 rounded-sm"
              >
                <img src={BEAT_TOKENS_LOGO} alt="Beat Tokens" className="h-16 w-16 sm:h-24 sm:w-24 object-contain mb-4" />
                <p className="text-white text-sm sm:text-base" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{t('home.stats.earnBeatTokens')}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cymbal Series Showcase */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/rf2u35tg_g1.webp)` 
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/70" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <p className="text-sm tracking-[0.4em] text-[#D4AF37] mb-4 uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>{t('home.collection.eyebrow')}</p>
              <h2 
                className="text-5xl lg:text-6xl tracking-wider"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {t('home.collection.title')}
              </h2>
            </div>
            <Link to="/shop" data-testid="view-all-cymbals">
              <Button 
                variant="outline" 
                className="mt-6 lg:mt-0 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm"
              >
                {t('home.collection.viewAll')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          {/* Series Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CYMBAL_SERIES.map((series, idx) => (
              <motion.div
                key={series.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="group"
              >
                <Link to={`/shop?series=${series.name}`} data-testid={`series-${series.name.toLowerCase()}`}>
                  <div className="relative aspect-square overflow-hidden rounded-sm border border-zinc-800 bg-[#050505] transition-all duration-500 group-hover:border-[#D4AF37]/50">
                    <img 
                      src={series.image} 
                      alt={series.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 
                        className="text-3xl text-white tracking-wider"
                        style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
                      >
                        {series.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Artists Testimonial */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{ 
            backgroundImage: `url(${HERO_IMAGES.texture})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.4em] text-zinc-500 mb-8 uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>{t('home.testimonial.eyebrow')}</p>
            <blockquote 
              className="text-3xl lg:text-4xl text-white mb-8 leading-relaxed italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              "{t('home.testimonial.quote')}"
            </blockquote>
            <p className="text-[#D4AF37]" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}>— TOBY VENTURA, {t('home.testimonial.role')}</p>
          </motion.div>
        </div>
      </section>

      {/* Featured Cymbal Packs */}
      <FeaturedPacksCarousel />

      {/* Featured Artists Carousel */}
      <FeaturedArtistsCarousel />

      {/* Featured Videos Section */}
      <FeaturedVideosSection />

      {/* Latest Blog Posts */}
      <BlogShowcase />

      {/* Membership Tiers Section */}
      <div id="memberships">
        <MembershipSection />
      </div>

      {/* Loyalty Rewards Explainer Section */}
      <section className="py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden bg-[#050505]">
        {/* Subtle background pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #D4AF37 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.4em] text-zinc-500 mb-4 uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.2em' }}>
              {t('home.rewards.eyebrow')}
            </p>
            <h2 
              className="text-5xl lg:text-6xl tracking-wider mb-6"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.rewards.title').split(' ').slice(0, -1).join(' ')}
              <span className="block bg-gradient-to-r from-[#D4AF37] via-[#F0C84B] to-[#22C55E] bg-clip-text text-transparent">
                {t('home.rewards.title').split(' ').slice(-1)[0]}
              </span>
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {t('home.rewards.description')}
            </p>
          </motion.div>

          {/* Two Column Loyalty Cards */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Gold Dust Card - Cymbals */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-sm blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-gradient-to-br from-[#1a1508] via-[#0a0a0a] to-[#0a0a0a] border border-[#D4AF37]/30 rounded-sm p-8 lg:p-10 h-full">
                {/* Header */}
                <div className="flex items-start gap-6 mb-8">
                  <img 
                    src={GOLD_DUST_LOGO} 
                    alt="Gold Dust" 
                    className="w-20 h-20 lg:w-24 lg:h-24 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                  />
                  <div>
                    <h3 
                      className="text-3xl lg:text-4xl text-[#D4AF37] mb-2"
                      style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
                    >
                      {t('home.rewards.goldDustTitle')}
                    </h3>
                    <p className="text-zinc-400 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {t('home.rewards.goldDustSubtitle')}
                    </p>
                  </div>
                </div>
                
                {/* How it works */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>1</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step1Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step1GoldDust')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>2</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step2Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step2Desc')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>3</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step3Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step3Desc')}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Reward Tiers */}
                <div className="bg-black/30 border border-[#D4AF37]/10 rounded-sm p-4 mb-6">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t('home.rewards.redemptionTiers')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <p className="text-2xl text-[#D4AF37]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>500</p>
                      <p className="text-xs text-zinc-500">= £10 OFF</p>
                    </div>
                    <div className="text-center border-x border-[#D4AF37]/10">
                      <p className="text-2xl text-[#D4AF37]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>1,000</p>
                      <p className="text-xs text-zinc-500">= £24 OFF</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl text-[#D4AF37]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>2,500</p>
                      <p className="text-xs text-zinc-500">= £70 OFF</p>
                    </div>
                  </div>
                </div>
                
                {/* Referral Bonus */}
                <div className="flex items-center gap-3 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm">
                  <Sparkles className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                  <p className="text-sm text-zinc-300" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    <span className="text-[#D4AF37] font-bold">{t('home.rewards.referFriend')}</span> {t('home.rewards.referGoldDust')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Beat Tokens Card - Drumsticks */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#22C55E]/20 to-transparent rounded-sm blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative bg-gradient-to-br from-[#0a1f18] via-[#050505] to-[#050505] border border-[#22C55E]/30 rounded-sm p-8 lg:p-10 h-full">
                {/* Header */}
                <div className="flex items-start gap-6 mb-8">
                  <img 
                    src={BEAT_TOKENS_LOGO} 
                    alt="Beat Tokens" 
                    className="w-20 h-20 lg:w-24 lg:h-24 object-contain drop-shadow-[0_0_20px_rgba(34,197,94,0.3)]"
                  />
                  <div>
                    <h3 
                      className="text-3xl lg:text-4xl text-[#22C55E] mb-2"
                      style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
                    >
                      {t('home.rewards.beatTokensTitle')}
                    </h3>
                    <p className="text-zinc-400 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {t('home.rewards.beatTokensSubtitle')}
                    </p>
                  </div>
                </div>
                
                {/* How it works */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#22C55E] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>1</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step1Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step1BeatTokens')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#22C55E] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>2</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step2Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step2Desc')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 border border-[#22C55E]/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#22C55E] font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>3</span>
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">{t('home.rewards.step3Title')}</p>
                      <p className="text-zinc-500 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        {t('home.rewards.step3Desc')}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Reward Tiers */}
                <div className="bg-black/30 border border-[#22C55E]/10 rounded-sm p-4 mb-6">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{t('home.rewards.redemptionTiers')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <p className="text-2xl text-[#22C55E]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>300</p>
                      <p className="text-xs text-zinc-500">= £5 OFF</p>
                    </div>
                    <div className="text-center border-x border-[#22C55E]/10">
                      <p className="text-2xl text-[#22C55E]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>600</p>
                      <p className="text-xs text-zinc-500">= £12 OFF</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl text-[#22C55E]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>1,500</p>
                      <p className="text-xs text-zinc-500">= £35 OFF</p>
                    </div>
                  </div>
                </div>
                
                {/* Referral Bonus */}
                <div className="flex items-center gap-3 p-4 bg-[#22C55E]/10 border border-[#22C55E]/20 rounded-sm">
                  <Drum className="h-5 w-5 text-[#22C55E] flex-shrink-0" />
                  <p className="text-sm text-zinc-300" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    <span className="text-[#22C55E] font-bold">{t('home.rewards.referFriend')}</span> {t('home.rewards.referBeatTokens')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/customer/auth" data-testid="loyalty-signup-cta">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-[#D4AF37] to-[#22C55E] hover:from-[#b8962e] hover:to-[#1ea34d] text-black px-10 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.1em' }}
              >
                {t('home.rewards.createAccount')}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <p className="text-zinc-600 text-sm mt-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {t('home.rewards.startEarning')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* B2B / Trade / Artists Portal CTA */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts/e7m2rwa3_image.png)` 
          }}
        />
        
        {/* Dark overlay with gold tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-[#1a1408]/80" />
        
        {/* Gold accent glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D4AF37]/10 blur-[120px] rounded-full" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-8">
              <Sparkles className="h-4 w-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {t('home.tradePortal.eyebrow')}
              </span>
            </div>
            
            {/* Main headline */}
            <h2 
              className="text-5xl md:text-6xl lg:text-7xl text-white mb-6 tracking-wider"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              {t('home.tradePortal.title').split(' ').slice(0, -1).join(' ')}
              <span className="block text-[#D4AF37]">{t('home.tradePortal.title').split(' ').slice(-1)[0]}</span>
            </h2>
            
            {/* Description */}
            <p 
              className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-12 leading-relaxed"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              {t('home.tradePortal.description')}
            </p>
            
            {/* Feature pills */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[
                "Wholesale Pricing",
                "Custom Quotes", 
                "Artist Endorsements",
                "Bulk Orders",
                "Priority Support"
              ].map((feature, idx) => (
                <motion.span
                  key={feature}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-zinc-300"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {feature}
                </motion.span>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login" data-testid="b2b-login-cta">
                <Button 
                  size="lg"
                  className="bg-[#D4AF37] hover:bg-[#b8962e] text-black px-10 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105 shadow-lg shadow-[#D4AF37]/20"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.15em' }}
                >
                  {t('home.tradePortal.b2bLogin')}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
              <a 
                href="mailto:will@templeofsound.co.uk?subject=Trade%20Account%20Enquiry" 
                data-testid="b2b-enquiry-cta"
              >
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-10 py-6 text-lg rounded-sm transition-all duration-300"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.15em' }}
                >
                  Apply for Trade Account
                </Button>
              </a>
            </div>
            
            {/* Trust indicator */}
            <p className="mt-8 text-zinc-600 text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Already trusted by 200+ retailers and professional artists worldwide
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="mb-4">
                <img src={TOS_LOGO_WHITE} alt="Temple of Sound" className="h-10 w-auto" />
              </div>
              <p className="text-zinc-500 text-sm max-w-md" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                Premium percussion instruments for professionals. Handcrafted in London since 2010.
              </p>
            </div>
            
            {/* Newsletter Signup */}
            <div className="md:col-span-1">
              <NewsletterSignup variant="footer" />
            </div>
            
            <div>
              <h5 className="text-sm tracking-wider text-zinc-400 uppercase mb-4">Shop</h5>
              <ul className="space-y-3">
                <li><Link to="/shop" className="text-zinc-500 hover:text-[#D4AF37] transition-colors text-sm">Cymbals</Link></li>
                <li><Link to="/drumsticks" className="text-zinc-500 hover:text-[#22C55E] transition-colors text-sm">Drumsticks</Link></li>
                <li><Link to="/custom-drumsticks" className="text-zinc-500 hover:text-[#22C55E] transition-colors text-sm">Custom Builder</Link></li>
                <li><Link to="/blog" className="text-zinc-500 hover:text-[#D4AF37] transition-colors text-sm" data-testid="footer-blog-link">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-sm tracking-wider text-zinc-400 uppercase mb-4">Account</h5>
              <ul className="space-y-3">
                <li><Link to="/customer/auth" className="text-zinc-500 hover:text-white transition-colors text-sm">Sign In</Link></li>
                <li><Link to="/account" className="text-zinc-500 hover:text-white transition-colors text-sm">My Orders</Link></li>
                <li><Link to="/account" className="text-zinc-500 hover:text-[#D4AF37] transition-colors text-sm">Gold Dust</Link></li>
                <li><Link to="/account" className="text-zinc-500 hover:text-[#22C55E] transition-colors text-sm">Beat Tokens</Link></li>
                <li><Link to="/contact" className="text-zinc-500 hover:text-[#D4AF37] transition-colors text-sm">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-600 text-xs">© 2026 Temple of Sound. All rights reserved.</p>
            <BoostCredit />
            <div className="flex items-center gap-6">
              <span className="text-zinc-600 text-xs">Secure payments with Stripe</span>
            </div>
          </div>
        </div>
      </footer>
      </main>
    </div>
  );
}
